import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import {
  addActive,
  getActive,
  listRosterOptions,
  updateActive,
} from '../../services/activeService';
import { sendAppLoginEmail, setAppCredentials } from '../../services/driverService';

const defaultForm = {
  driverId: '',
  driverMongoId: '',
  enrollmeDriverId: '',
  appDriverRef: '',
  cabNumber: '',
  enrollmeVehicleId: '',
  firstName: '',
  lastName: '',
  licPlates: '',
  make: '',
  model: '',
  color: '',
  status: 'Active',
  lat: '',
  lng: '',
  hoursOfService: {
    totalHours: '',
    drivingHours: '',
    breakMinutes: '',
  },
};

const formatDateTime = (value) => {
  if (!value) return 'Never';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return 'Unknown';
    return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
  } catch (_err) {
    return 'Unknown';
  }
};

const formatRosterStatus = (value) =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const ActiveManage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [form, setForm] = useState(defaultForm);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [driverAppMeta, setDriverAppMeta] = useState({
    forcePasswordReset: true,
    lastLoginAt: null,
    lastLogoutAt: null,
    blockedAt: null,
    blockedBy: null,
    blockedReason: '',
  });
  const [appPassword, setAppPassword] = useState('');
  const [appPasswordConfirm, setAppPasswordConfirm] = useState('');
  const [forcePasswordReset, setForcePasswordReset] = useState(true);
  const [appAccessBlocked, setAppAccessBlocked] = useState(false);
  const [appAccessBlockReason, setAppAccessBlockReason] = useState('');
  const [appSaving, setAppSaving] = useState(false);
  const [appError, setAppError] = useState('');
  const [appMessage, setAppMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    const fetchOptions = async () => {
      setLoading(true);
      setError('');
      try {
        const optionsRes = await listRosterOptions();
        const driverList = optionsRes.data?.drivers || [];
        const vehicleList = optionsRes.data?.vehicles || [];
        setDrivers(Array.isArray(driverList) ? driverList : []);
        setVehicles(Array.isArray(vehicleList) ? vehicleList : []);
      } catch (err) {
        const msg = err.response?.data?.message || 'Unable to fetch roster options';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, []);

  useEffect(() => {
    if (!isEditing) return;

    const fetchActive = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await getActive(id);
        const data = res.data?.active || res.data?.data || res.data;
        if (data && typeof data === 'object') {
          setForm({
            driverId: data.driverId || '',
            enrollmeDriverId: data.enrollmeDriverId || '',
            driverMongoId: data.enrollmeDriverId || data.driverId || '',
            appDriverRef: data.driverId || '',
            cabNumber: data.cabNumber || '',
            enrollmeVehicleId: data.enrollmeVehicleId || '',
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            licPlates: data.licPlates || '',
            make: data.make || '',
            model: data.model || '',
            color: data.color || '',
            status: data.status || 'Active',
            lat: data.currentLocation?.coordinates?.[1]?.toString() || '',
            lng: data.currentLocation?.coordinates?.[0]?.toString() || '',
            hoursOfService: {
              totalHours: data.hoursOfService?.totalHours?.toString() || '',
              drivingHours: data.hoursOfService?.drivingHours?.toString() || '',
              breakMinutes: data.hoursOfService?.breakMinutes?.toString() || '',
            },
          });
        }
      } catch (err) {
        const msg = err.response?.data?.message || 'Unable to load active record';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchActive();
  }, [id, isEditing]);


  useEffect(() => {
    if (!drivers.length || (!form.driverId && !form.driverMongoId && !form.enrollmeDriverId)) return;
    const selected = drivers.find((driver) => {
      if (form.enrollmeDriverId || form.driverMongoId) {
        const selectedId = form.enrollmeDriverId || form.driverMongoId;
        return driver._id === selectedId || driver.id === selectedId || driver.driverId === selectedId;
      }
      return driver.driverId === form.driverId;
    });
    if (!selected) return;

    setForm((prev) => {
      const nextMongoId = selected._id || selected.id || prev.driverMongoId;
      const nextDriverId = selected.driverId || '';
      if (prev.driverMongoId && prev.driverMongoId === nextMongoId && prev.driverId === nextDriverId) {
        return prev;
      }
      return {
        ...prev,
        driverMongoId: nextMongoId,
        enrollmeDriverId: nextMongoId,
        appDriverRef: selected.dispatchDriverMongoId || selected.driverId || prev.appDriverRef,
        driverId: nextDriverId,
        firstName: selected.firstName || prev.firstName,
        lastName: selected.lastName || prev.lastName,
      };
    });

    setDriverAppMeta({
      forcePasswordReset: Boolean(selected?.driverApp?.forcePasswordReset),
      lastLoginAt: selected?.driverApp?.lastLoginAt || null,
      lastLogoutAt: selected?.driverApp?.lastLogoutAt || null,
      blockedAt: selected?.driverApp?.blockedAt || null,
      blockedBy: selected?.driverApp?.blockedBy || null,
      blockedReason: selected?.driverApp?.blockedReason || '',
    });
    setForcePasswordReset(Boolean(selected?.driverApp?.forcePasswordReset));
    setAppAccessBlocked(Boolean(selected?.driverApp?.blockedAt));
    setAppAccessBlockReason(selected?.driverApp?.blockedReason || '');
  }, [drivers, form.driverId, form.driverMongoId, form.enrollmeDriverId]);

  const driverOptions = useMemo(
    () =>
      drivers.map((driver) => {
        const fullName = `${driver.firstName || ''} ${driver.lastName || ''}`.trim();
        const labelBase = fullName || driver.email || 'Unnamed driver';
        const idSuffix = driver.driverId ? ` (${driver.driverId})` : ' (EnrollMe)';
        const statusParts = [
          driver.status && driver.status !== 'active' ? formatRosterStatus(driver.status) : '',
          driver.enrollmentStatus && driver.enrollmentStatus !== 'completed'
            ? formatRosterStatus(driver.enrollmentStatus)
            : '',
          driver.profileCompleted === false ? 'Profile incomplete' : '',
        ].filter(Boolean);
        const statusSuffix = statusParts.length ? ` - ${statusParts.join(', ')}` : '';
        return {
          value: driver._id || driver.id,
          label: `${labelBase}${idSuffix}${statusSuffix}`,
          raw: driver,
        };
      }),
    [drivers],
  );

  const vehicleOptions = useMemo(
    () => vehicles.map((vehicle) => ({
      value: vehicle._id || vehicle.id || vehicle.cabNumber,
      label: `Cab ${vehicle.cabNumber || vehicle._id?.slice(-4) || ''}${vehicle.status && vehicle.status !== 'active' ? ` - ${formatRosterStatus(vehicle.status)}` : ''}${vehicle.assignedDriverId ? ' - assigned in EnrollMe' : ''}`.trim(),
      raw: vehicle,
    })),
    [vehicles],
  );

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name.startsWith('hoursOfService.')) {
      const key = name.split('.')[1];
      setForm((prev) => ({
        ...prev,
        hoursOfService: {
          ...prev.hoursOfService,
          [key]: value,
        },
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDriverSelect = (event) => {
    const { value } = event.target;
    const selected = driverOptions.find((option) => option.value === value);
    setForm((prev) => ({
      ...prev,
      driverMongoId: value,
      enrollmeDriverId: selected?.raw?._id || selected?.raw?.id || value,
      appDriverRef: selected?.raw?.dispatchDriverMongoId || selected?.raw?.driverId || '',
      driverId: selected?.raw?.driverId || '',
      firstName: selected?.raw?.firstName || prev.firstName,
      lastName: selected?.raw?.lastName || prev.lastName,
    }));
    if (selected?.raw) {
      setDriverAppMeta({
        forcePasswordReset: Boolean(selected.raw.driverApp?.forcePasswordReset),
        lastLoginAt: selected.raw.driverApp?.lastLoginAt || null,
        lastLogoutAt: selected.raw.driverApp?.lastLogoutAt || null,
        blockedAt: selected.raw.driverApp?.blockedAt || null,
        blockedBy: selected.raw.driverApp?.blockedBy || null,
        blockedReason: selected.raw.driverApp?.blockedReason || '',
      });
      setForcePasswordReset(Boolean(selected.raw.driverApp?.forcePasswordReset));
      setAppAccessBlocked(Boolean(selected.raw.driverApp?.blockedAt));
      setAppAccessBlockReason(selected.raw.driverApp?.blockedReason || '');
      setAppPassword("");
      setAppPasswordConfirm("");
      setAppError("");
      setAppMessage("");
    }
  };

  const handleVehicleSelect = (event) => {
    const { value } = event.target;
    const selected = vehicleOptions.find((option) => option.value === value);
    setForm((prev) => ({
      ...prev,
      enrollmeVehicleId: selected?.raw?._id || selected?.raw?.id || '',
      cabNumber: selected?.raw?.cabNumber || value,
      licPlates: selected?.raw?.licPlates || prev.licPlates,
      make: selected?.raw?.make || prev.make,
      model: selected?.raw?.model || prev.model,
      color: selected?.raw?.color || prev.color,
    }));
  };

  const resetAppForm = () => {
    setAppPassword("");
    setAppPasswordConfirm("");
    setAppError("");
  };

  const handleSendAppLoginEmail = async () => {
    setEmailError('');
    setEmailMessage('');
    if (!form.appDriverRef) {
      setEmailError('Save this active assignment first before sending credentials.');
      return;
    }
    setEmailSending(true);
    try {
      const res = await sendAppLoginEmail(form.appDriverRef);
      setEmailMessage(res.data?.message || 'App credentials emailed to driver.');
      const driver = res.data?.driver || res.data?.data || res.data;
      if (driver?.driverApp) {
        setDriverAppMeta({
          forcePasswordReset: Boolean(driver.driverApp.forcePasswordReset),
          lastLoginAt: driver.driverApp.lastLoginAt || null,
          lastLogoutAt: driver.driverApp.lastLogoutAt || null,
          blockedAt: driver.driverApp.blockedAt || null,
          blockedBy: driver.driverApp.blockedBy || null,
          blockedReason: driver.driverApp.blockedReason || '',
        });
      }
    } catch (err) {
      setEmailError(err.response?.data?.message || 'Failed to send credentials email.');
    } finally {
      setEmailSending(false);
    }
  };

  const selectedVehicleValue = form.enrollmeVehicleId || form.cabNumber;
  const selectedDriverValue = form.enrollmeDriverId || form.driverMongoId;
  const hasSelectedDriverOption = driverOptions.some((option) => option.value === selectedDriverValue);
  const hasSelectedVehicleOption = vehicleOptions.some((option) => option.value === selectedVehicleValue);

  const handleAppCredentialsSubmit = async (event) => {
    event.preventDefault();
    setAppError("");
    setAppMessage("");

    if (!form.appDriverRef) {
      setAppError("Save this active assignment first so TaxiOps can link the EnrollMe driver to mobile access.");
      return;
    }

    if ((appPassword || appPasswordConfirm) && appPassword.trim().length < 8) {
      setAppError("Password must be at least 8 characters long when provided.");
      return;
    }

    if ((appPassword || appPasswordConfirm) && appPassword !== appPasswordConfirm) {
      setAppError("Passwords do not match.");
      return;
    }

    setAppSaving(true);
    try {
      const res = await setAppCredentials(form.appDriverRef, {
        ...(appPassword ? { password: appPassword } : {}),
        forcePasswordReset,
        appAccessBlocked,
        appAccessBlockReason,
      });
      const driver = res.data?.driver || res.data?.data || res.data;
      if (driver?.driverApp) {
        setDriverAppMeta({
          forcePasswordReset: Boolean(driver.driverApp.forcePasswordReset),
          lastLoginAt: driver.driverApp.lastLoginAt || null,
          lastLogoutAt: driver.driverApp.lastLogoutAt || null,
          blockedAt: driver.driverApp.blockedAt || null,
          blockedBy: driver.driverApp.blockedBy || null,
          blockedReason: driver.driverApp.blockedReason || '',
        });
        setForcePasswordReset(Boolean(driver.driverApp.forcePasswordReset));
        setAppAccessBlocked(Boolean(driver.driverApp.blockedAt));
        setAppAccessBlockReason(driver.driverApp.blockedReason || '');
      }
      setAppMessage("Driver mobile access updated.");
      resetAppForm();
    } catch (err) {
      const msg = err.response?.data?.message || "Unable to update driver credentials.";
      setAppError(msg);
    } finally {
      setAppSaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSaving(true);

    if ((!form.driverId && !form.enrollmeDriverId) || (!form.cabNumber && !form.enrollmeVehicleId)) {
      setError('Select a driver and vehicle before saving.');
      setSaving(false);
      return;
    }

    const payload = {
      driverId: form.driverId,
      enrollmeDriverId: form.enrollmeDriverId,
      cabNumber: form.cabNumber,
      enrollmeVehicleId: form.enrollmeVehicleId,
      firstName: form.firstName,
      lastName: form.lastName,
      licPlates: form.licPlates,
      make: form.make,
      model: form.model,
      color: form.color,
      status: form.status,
      hoursOfService: {
        totalHours: form.hoursOfService.totalHours ? Number(form.hoursOfService.totalHours) : undefined,
        drivingHours: form.hoursOfService.drivingHours ? Number(form.hoursOfService.drivingHours) : undefined,
        breakMinutes: form.hoursOfService.breakMinutes ? Number(form.hoursOfService.breakMinutes) : undefined,
      },
    };

    if (form.lat && form.lng) {
      payload.lat = Number(form.lat);
      payload.lng = Number(form.lng);
    }

    if (!payload.hoursOfService.totalHours && !payload.hoursOfService.drivingHours && !payload.hoursOfService.breakMinutes) {
      delete payload.hoursOfService;
    }

    try {
      if (isEditing) {
        await updateActive(id, payload);
      } else {
        await addActive(payload);
      }
      navigate('/actives');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to save active record';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const actions = (
    <>
      <Link to="/actives" className="btn btn-ghost">
        Back to roster
      </Link>
    </>
  );

  const title = isEditing ? 'Update active assignment' : 'Activate a driver';
  const subtitle = isEditing
    ? 'Tune the roster assignment and duty metrics for this driver.'
    : 'Pair a compliant driver with a ready vehicle. They come online from the driver app.';

  return (
    <AppLayout title={title} subtitle={subtitle} actions={actions}>
      <div className="surface">
        {loading ? (
          <div className="skeleton" style={{ height: '440px' }} />
        ) : (
          <>
            <form onSubmit={handleSubmit}>
              {error && <div className="feedback error">{error}</div>}
              <div className="form-section">
                <div>
                  <h3>Roster selection</h3>
                  <p>Pull drivers and vehicles directly from EnrollMe, including incomplete profiles.</p>
                </div>
                <div className="form-grid">
                  <div>
                    <label htmlFor="driverId">Driver</label>
                    <select id="driverId" name="driverMongoId" value={selectedDriverValue} onChange={handleDriverSelect} required>
                      <option value="">Select driver.</option>
                      {selectedDriverValue && !hasSelectedDriverOption && (
                        <option value={selectedDriverValue}>
                          {`${form.firstName || ''} ${form.lastName || ''}`.trim() || form.driverId || 'Current driver'}
                        </option>
                      )}
                      {driverOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="cabNumber">Cab</label>
                    <select id="cabNumber" name="enrollmeVehicleId" value={selectedVehicleValue} onChange={handleVehicleSelect} required>
                      <option value="">Select cab.</option>
                      {selectedVehicleValue && !hasSelectedVehicleOption && (
                        <option value={selectedVehicleValue}>Cab {form.cabNumber}</option>
                      )}
                      {vehicleOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="firstName">First name</label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={form.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName">Last name</label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={form.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="licPlates">License plate</label>
                    <input
                      id="licPlates"
                      name="licPlates"
                      type="text"
                      value={form.licPlates}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="make">Make</label>
                    <input id="make" name="make" type="text" value={form.make} onChange={handleInputChange} />
                  </div>
                  <div>
                    <label htmlFor="model">Model</label>
                    <input id="model" name="model" type="text" value={form.model} onChange={handleInputChange} />
                  </div>
                  <div>
                    <label htmlFor="color">Color</label>
                    <input id="color" name="color" type="text" value={form.color} onChange={handleInputChange} />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div>
                  <h3>Roster status</h3>
                  <p>Availability is controlled by the driver app when the driver signs in and starts a shift.</p>
                </div>
                <div className="form-grid">
                  <div>
                    <label htmlFor="status">Status</label>
                    <select id="status" name="status" value={form.status} onChange={handleInputChange}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="lat">Latitude</label>
                    <input
                      id="lat"
                      name="lat"
                      type="number"
                      step="0.000001"
                      value={form.lat}
                      onChange={handleInputChange}
                      placeholder="39.7392"
                    />
                  </div>
                  <div>
                    <label htmlFor="lng">Longitude</label>
                    <input
                      id="lng"
                      name="lng"
                      type="number"
                      step="0.000001"
                      value={form.lng}
                      onChange={handleInputChange}
                      placeholder="-104.9903"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <div>
                  <h3>Hours of service</h3>
                  <p>Log the driver's on-duty totals to enforce compliance.</p>
                </div>
                <div className="form-grid">
                  <div>
                    <label htmlFor="hoursOfService.totalHours">Total hours on duty</label>
                    <input
                      id="hoursOfService.totalHours"
                      name="hoursOfService.totalHours"
                      type="number"
                      step="0.1"
                      value={form.hoursOfService.totalHours}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="hoursOfService.drivingHours">Driving hours</label>
                    <input
                      id="hoursOfService.drivingHours"
                      name="hoursOfService.drivingHours"
                      type="number"
                      step="0.1"
                      value={form.hoursOfService.drivingHours}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label htmlFor="hoursOfService.breakMinutes">Break minutes</label>
                    <input
                      id="hoursOfService.breakMinutes"
                      name="hoursOfService.breakMinutes"
                      type="number"
                      step="1"
                      value={form.hoursOfService.breakMinutes}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              <div className="form-footer">
                <div />
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving.' : isEditing ? 'Update active' : 'Activate driver'}
                </button>
              </div>
            </form>

            {form.driverMongoId && (
              <div className="form-section">
                <div className="form-section-header">
                  <div>
                    <h3>Mobile app access</h3>
                    <p>Manage login access and send app credentials to this driver.</p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={handleSendAppLoginEmail}
                    disabled={emailSending || !form.appDriverRef}
                    title={!form.appDriverRef ? 'Save this active assignment first' : 'Generates a new password and emails it to the driver with App Store / Play Store links'}
                  >
                    {emailSending ? 'Sending...' : 'Send app credentials'}
                  </button>
                </div>

                <div className="app-access-status">
                  <div className="app-access-stat">
                    <span className="app-access-stat-label">Driver ID</span>
                    <span className="app-access-stat-value">{form.driverId || 'Pending'}</span>
                  </div>
                  <div className="app-access-stat">
                    <span className="app-access-stat-label">Last login</span>
                    <span className="app-access-stat-value">{formatDateTime(driverAppMeta.lastLoginAt)}</span>
                  </div>
                  <div className="app-access-stat">
                    <span className="app-access-stat-label">Last logout</span>
                    <span className="app-access-stat-value">{formatDateTime(driverAppMeta.lastLogoutAt)}</span>
                  </div>
                  <div className="app-access-stat">
                    <span className="app-access-stat-label">Force reset</span>
                    <span className="app-access-stat-value">{driverAppMeta.forcePasswordReset ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="app-access-stat">
                    <span className="app-access-stat-label">Login access</span>
                    <span className={`app-access-badge ${driverAppMeta.blockedAt ? 'app-access-badge--blocked' : 'app-access-badge--allowed'}`}>
                      {driverAppMeta.blockedAt ? 'Blocked' : 'Allowed'}
                    </span>
                  </div>
                </div>

                {driverAppMeta.blockedAt && (
                  <div className="feedback error">
                    Blocked {formatDateTime(driverAppMeta.blockedAt)}{driverAppMeta.blockedReason ? ` — ${driverAppMeta.blockedReason}` : ''}
                  </div>
                )}

                {emailError && <div className="feedback error">{emailError}</div>}
                {emailMessage && <div className="feedback success">{emailMessage}</div>}

                <form onSubmit={handleAppCredentialsSubmit}>
                  <div className="form-grid two-column">
                    <div>
                      <label htmlFor="appPassword">New password</label>
                      <input
                        id="appPassword"
                        type="password"
                        value={appPassword}
                        onChange={(event) => setAppPassword(event.target.value)}
                        placeholder="Leave blank to keep current password"
                      />
                    </div>
                    <div>
                      <label htmlFor="appPasswordConfirm">Confirm password</label>
                      <input
                        id="appPasswordConfirm"
                        type="password"
                        value={appPasswordConfirm}
                        onChange={(event) => setAppPasswordConfirm(event.target.value)}
                        placeholder="Repeat the new password"
                      />
                    </div>
                    <label className="checkbox-field full-width">
                      <input
                        type="checkbox"
                        checked={forcePasswordReset}
                        onChange={(event) => setForcePasswordReset(event.target.checked)}
                      />
                      Require driver to change password on next login
                    </label>
                    <label className="checkbox-field full-width">
                      <input
                        type="checkbox"
                        checked={appAccessBlocked}
                        onChange={(event) => setAppAccessBlocked(event.target.checked)}
                      />
                      Block this driver from logging in to the mobile app
                    </label>
                    {appAccessBlocked && (
                      <div className="full-width">
                        <label htmlFor="appAccessBlockReason">Block reason</label>
                        <textarea
                          id="appAccessBlockReason"
                          value={appAccessBlockReason}
                          onChange={(event) => setAppAccessBlockReason(event.target.value)}
                          rows="3"
                          placeholder="Reason shown internally and used for the sign-out notice"
                        />
                      </div>
                    )}
                  </div>
                  {appError && <div className="feedback error" style={{ marginTop: 12 }}>{appError}</div>}
                  {appMessage && <div className="feedback success" style={{ marginTop: 12 }}>{appMessage}</div>}
                  <div className="button-row" style={{ marginTop: 16 }}>
                    <button type="submit" className="btn btn-primary" disabled={appSaving}>
                      {appSaving ? 'Saving...' : 'Save mobile access'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={resetAppForm}
                      disabled={appSaving}
                    >
                      Clear
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}

      </div>
    </AppLayout>
  );
};

export default ActiveManage;
